/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {useMemo} from 'react'
import {useCustomer, useCustomerId, useCustomerType} from '@salesforce/commerce-sdk-react'
import useOcapiSite from './useOcapiSite'
import {isD2BCustomer as computeIsD2BCustomer} from '../utils/d2b-utils'

/**
 * A hook that returns the current customer.
 *
 */
export const useCurrentCustomer = () => {
    const customerId = useCustomerId()
    const {isRegistered, isGuest, customerType} = useCustomerType()
    const {d2bEnabled, taxationPolicy} = useOcapiSite()
    const query = useCustomer({parameters: {customerId}}, {enabled: !!customerId && isRegistered})

    const memoizedData = useMemo(() => {
        const isD2BCustomer = computeIsD2BCustomer(d2bEnabled, query.data)
        const isCustomNetPriceEnabled = isD2BCustomer && taxationPolicy === 'gross'
        const isProductionTestingTeamCustomer = query.data?.c_isProductionTestingTeamCustomer

        return {
            ...query.data,
            customerType,
            customerId,
            isRegistered,
            isGuest,
            isD2BCustomer,
            isProductionTestingTeamCustomer,
            isCustomNetPriceEnabled
        }
    }, [customerId, isRegistered, isGuest, customerType, d2bEnabled, taxationPolicy, query.data])

    return {...query, data: memoizedData}
}
